import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  CircularProgress,
  Collapse,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonGroup,
  IconButton,
} from "@mui/material";
import { toast } from "react-hot-toast";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import "dayjs/locale/ja";
import {
  Print as PrintIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TableRestaurant as TableIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import api from "../../services/api";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const OrderHistory = () => {
  const { storeId } = useParams();
  const [orders, setOrders] = useState([]);
  const [selectedTableGroup, setSelectedTableGroup] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [startDate, setStartDate] = useState(dayjs().startOf("day"));
  const [endDate, setEndDate] = useState(dayjs().endOf("day"));
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [expandedTables, setExpandedTables] = useState({});

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, startDate, endDate]);

  const fetchOrders = async () => {
    try {
      const response = await api.get(`/api/orders`, {
        params: {
          storeId,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setOrders(data);
    } catch (error) {
      console.error("注文履歴の取得に失敗しました:", error);
    }
  };

  // テーブル単位でグループ化
  const tableGroups = useMemo(() => {
    const groups = {};

    orders.forEach(order => {
      const sessionId = order.sessionId || order.session_id;
      const tableSession = order.tableSessions || order.table_sessions;
      const paymentDetails = order.paymentDetails || order.payment_details || {};
      // テーブル番号: table_sessions > payment_details > order直接
      const tableNumber = tableSession?.tableNumber || tableSession?.table_number || paymentDetails.tableNumber || order.tableNumber;
      // エリア: table_sessions > payment_details > order直接
      const area = tableSession?.area || paymentDetails.area || order.area || "";
      const created = order.createdAt || order.created_at;
      const orderDate = created ? new Date(created).toDateString() : '';

      // グループキー:
      // 1. session_idがあればそれを使用
      // 2. テーブル番号がある場合は、同じ日の同じテーブル番号・エリアでグループ化
      // 3. テーブル番号がない場合は、同じ日の「レジ注文」として1つにまとめる
      let groupKey;
      if (sessionId) {
        groupKey = sessionId;
      } else if (tableNumber) {
        groupKey = `table-${tableNumber}-${area}-${orderDate}`;
      } else {
        // テーブル番号がない注文は日付ごとに1つのグループにまとめる
        groupKey = `register-${orderDate}`;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = {
          sessionId,
          tableNumber: tableNumber || "-",
          area,
          orders: [],
          totalAmount: 0,
          tipTotal: 0,
          hasUnpaid: false,
          firstOrderTime: null,
          lastOrderTime: null,
        };
      }

      const orderTotal = order.total ?? order.totalAmount ?? order.total_amount ?? 0;
      // api.jsでsnake_case -> camelCase変換されるため、tipTotalを優先
      const orderTip = order.tipTotal ?? order.tip_total ?? 0;
      const paymentStatus = order.paymentStatus || order.payment_status;

      groups[groupKey].orders.push(order);
      groups[groupKey].totalAmount += orderTotal;
      groups[groupKey].tipTotal += orderTip;
      if (paymentStatus === 'UNPAID') {
        groups[groupKey].hasUnpaid = true;
      }

      if (created) {
        const orderTime = new Date(created);
        if (!groups[groupKey].firstOrderTime || orderTime < groups[groupKey].firstOrderTime) {
          groups[groupKey].firstOrderTime = orderTime;
        }
        if (!groups[groupKey].lastOrderTime || orderTime > groups[groupKey].lastOrderTime) {
          groups[groupKey].lastOrderTime = orderTime;
        }
      }
    });

    // 最新順にソート
    return Object.entries(groups)
      .sort((a, b) => (b[1].lastOrderTime || 0) - (a[1].lastOrderTime || 0))
      .map(([key, group]) => ({ key, ...group }));
  }, [orders]);


  const handleViewTableDetails = (tableGroup) => {
    setSelectedTableGroup(tableGroup);
    setDetailsDialog(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleRefund = async (orderId, type) => {
    setRefundProcessing(true);
    try {
      const order = selectedOrder || selectedTableGroup?.orders?.find(o => (o._id || o.id) === orderId);
      const amount = type === "full"
        ? (order?.total || order?.total_amount || 0)
        : (Number(refundAmount) || 0);

      if (type === "partial" && amount <= 0) {
        toast.error("返金額を入力してください");
        setRefundProcessing(false);
        return;
      }

      const paymentStatus = type === "full" ? "REFUNDED" : "PARTIALLY_REFUNDED";

      await api.patch(`/api/orders/${orderId}/payment-status`, {
        paymentStatus: paymentStatus,
        amount: amount,
        reason: type === "full" ? "全額返金" : "部分返金",
      });

      toast.success(type === "full" ? "全額返金しました" : `¥${amount.toLocaleString()}を返金しました`);
      await fetchOrders();
      setDetailsDialog(false);
      setRefundAmount(0);
    } catch (e) {
      console.error("返金に失敗:", e);
      toast.error("返金処理に失敗しました: " + (e.response?.data?.error || e.message));
    } finally {
      setRefundProcessing(false);
    }
  };

  const toggleTableExpand = (key) => {
    setExpandedTables(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(value);
  };


  const formatTime = (date) => {
    if (!date) return "-";
    return date.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTranslatedText = (translationObj, defaultLang = "ja") => {
    if (!translationObj) return "";
    if (typeof translationObj !== "object") return translationObj;
    return (
      translationObj[defaultLang] ||
      translationObj.ja ||
      translationObj.en ||
      translationObj.zh ||
      ""
    );
  };


  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case "PAID": return "支払済";
      case "UNPAID": return "未払い";
      case "REFUNDED": return "返金済";
      case "PARTIALLY_REFUNDED": return "一部返金";
      default: return status || "-";
    }
  };

  const getPaymentMethodLabel = (method) => {
    if (!method) return "-";
    const normalized = method.toLowerCase();
    switch (normalized) {
      case "cash": return "現金";
      case "credit_card":
      case "card": return "クレジットカード";
      case "qr":
      case "qr_code": return "QRコード決済";
      case "ic":
      case "transport_ic": return "交通系IC";
      case "paypay": return "PayPay";
      case "id": return "iD";
      case "quicpay": return "QUICPay";
      default: return method;
    }
  };

  // テーブルグループの全商品を取得
  const getAllItemsFromGroup = (tableGroup) => {
    const allItems = [];
    tableGroup.orders.forEach(order => {
      (order.items || order.order_items || []).forEach(item => {
        const product = item.product || item.products || {};
        const productName = product.name || item.product_name || item.name || '商品';
        const displayName = typeof productName === 'object' ? getTranslatedText(productName) : productName;
        const quantity = item.quantity || 1;
        const price = item.price || item.unit_price || 0;
        allItems.push({ displayName, quantity, price, orderId: order.id });
      });
    });
    return allItems;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        注文履歴（テーブル別）
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
        <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
          <DatePicker
            label="開始日"
            value={dayjs(startDate)}
            onChange={(d) => d && setStartDate(d.toDate())}
            slotProps={{ textField: { fullWidth: false } }}
          />
          <DatePicker
            label="終了日"
            value={dayjs(endDate)}
            onChange={(d) => d && setEndDate(d.toDate())}
            slotProps={{ textField: { fullWidth: false } }}
          />
        </Box>
      </LocalizationProvider>

      <Grid container spacing={2}>
        {tableGroups.map((group) => (
          <Grid item xs={12} md={6} lg={4} key={group.key}>
            <Card
              sx={{
                border: group.hasUnpaid ? '2px solid' : '1px solid',
                borderColor: group.hasUnpaid ? 'warning.main' : 'divider',
              }}
            >
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TableIcon color="primary" />
                    <Typography variant="h6">
                      {group.tableNumber !== "-" ? group.tableNumber : "レジ注文"}
                    </Typography>
                    {group.area && (
                      <Chip label={group.area} size="small" variant="outlined" />
                    )}
                  </Box>
                  <Chip
                    label={`${group.orders.length}件`}
                    size="small"
                    color={group.orders.length > 1 ? "primary" : "default"}
                  />
                </Box>

                <Typography variant="caption" color="text.secondary" display="block">
                  {formatTime(group.firstOrderTime)}
                  {group.orders.length > 1 && ` 〜 ${formatTime(group.lastOrderTime)}`}
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">合計</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(group.totalAmount)}
                    </Typography>
                  </Box>
                  {group.tipTotal > 0 && (
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="primary">投げ銭</Typography>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        {formatCurrency(group.tipTotal)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {group.hasUnpaid && (
                  <Chip
                    label="未払いあり"
                    color="warning"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}

                {/* 注文一覧（展開時） */}
                <Collapse in={expandedTables[group.key]}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>注文明細</Typography>
                  {group.orders.map((order, idx) => {
                    const orderId = order._id || order.id;
                    const paymentStatus = order.paymentStatus || order.payment_status;
                    const total = order.total ?? order.totalAmount ?? order.total_amount ?? 0;
                    const created = order.createdAt || order.created_at;
                    return (
                      <Box
                        key={orderId}
                        sx={{
                          p: 1,
                          mb: 1,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="body2">
                            {new Date(created).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {orderId?.slice(0, 8)}...
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(total)}
                          </Typography>
                          <Chip
                            label={getPaymentStatusLabel(paymentStatus)}
                            size="small"
                            color={paymentStatus === 'PAID' ? 'success' : paymentStatus === 'UNPAID' ? 'warning' : 'default'}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Collapse>
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button
                  size="small"
                  onClick={() => toggleTableExpand(group.key)}
                  endIcon={expandedTables[group.key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {expandedTables[group.key] ? "閉じる" : "詳細"}
                </Button>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleViewTableDetails(group)}
                    color="primary"
                  >
                    <ReceiptIcon />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {tableGroups.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            該当する注文がありません
          </Typography>
        </Paper>
      )}

      {/* テーブル詳細ダイアログ */}
      <Dialog
        open={detailsDialog}
        onClose={() => { setDetailsDialog(false); setSelectedTableGroup(null); setSelectedOrder(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedTableGroup ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TableIcon />
              {selectedTableGroup.tableNumber !== "-" ? `テーブル ${selectedTableGroup.tableNumber}` : "レジ注文"}
              {selectedTableGroup.area && (
                <Chip label={selectedTableGroup.area} size="small" />
              )}
            </Box>
          ) : "注文詳細"}
        </DialogTitle>
        <DialogContent>
          {selectedTableGroup && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {formatTime(selectedTableGroup.firstOrderTime)}
                {selectedTableGroup.orders.length > 1 && ` 〜 ${formatTime(selectedTableGroup.lastOrderTime)}`}
                {` ・ ${selectedTableGroup.orders.length}件の注文`}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>注文商品</Typography>
              <List dense>
                {getAllItemsFromGroup(selectedTableGroup).map((item, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={item.displayName}
                      secondary={`${item.quantity}点 × ${formatCurrency(item.price)}`}
                    />
                    <Typography>
                      {formatCurrency(item.price * item.quantity)}
                    </Typography>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6">合計</Typography>
                  <Typography variant="h6">{formatCurrency(selectedTableGroup.totalAmount)}</Typography>
                </Box>
                {selectedTableGroup.tipTotal > 0 && (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography color="primary">投げ銭</Typography>
                      <Typography color="primary">{formatCurrency(selectedTableGroup.tipTotal)}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="h6" color="primary">総計</Typography>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(selectedTableGroup.totalAmount + selectedTableGroup.tipTotal)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>注文明細</Typography>
              {selectedTableGroup.orders.map((order) => {
                const orderId = order._id || order.id;
                const paymentStatus = order.paymentStatus || order.payment_status;
                const paymentMethod = order.paymentDetails?.method || order.payment_details?.method || order.paymentMethod;
                const total = order.total ?? order.totalAmount ?? order.total_amount ?? 0;
                const created = order.createdAt || order.created_at;
                return (
                  <Box
                    key={orderId}
                    sx={{
                      p: 1,
                      mb: 0.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(total)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {created && new Date(created).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={getPaymentStatusLabel(paymentStatus)}
                        size="small"
                        color={paymentStatus === 'PAID' ? 'success' : paymentStatus === 'UNPAID' ? 'warning' : 'error'}
                      />
                      <Typography variant="caption">{getPaymentMethodLabel(paymentMethod)}</Typography>
                    </Box>
                  </Box>
                );
              })}

              {/* テーブル会計全体の返金操作 */}
              {selectedTableGroup.hasUnpaid && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>返金操作</Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      size="small"
                      type="number"
                      label="返金額"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      inputProps={{ min: 0 }}
                      sx={{ width: 120 }}
                    />
                    <ButtonGroup variant="outlined" size="small">
                      <Button
                        onClick={() => {
                          const unpaidOrder = selectedTableGroup.orders.find(o =>
                            (o.paymentStatus || o.payment_status) === 'UNPAID'
                          );
                          if (unpaidOrder) handleRefund(unpaidOrder._id || unpaidOrder.id, "partial");
                        }}
                        disabled={refundProcessing}
                      >
                        {refundProcessing ? <CircularProgress size={16} /> : "部分返金"}
                      </Button>
                      <Button
                        color="error"
                        onClick={() => {
                          const unpaidOrder = selectedTableGroup.orders.find(o =>
                            (o.paymentStatus || o.payment_status) === 'UNPAID'
                          );
                          if (unpaidOrder) handleRefund(unpaidOrder._id || unpaidOrder.id, "full");
                        }}
                        disabled={refundProcessing}
                      >
                        {refundProcessing ? <CircularProgress size={16} /> : "全額返金"}
                      </Button>
                    </ButtonGroup>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDetailsDialog(false); setSelectedTableGroup(null); setSelectedOrder(null); }}>
            閉じる
          </Button>
          <Button
            startIcon={<PrintIcon />}
            variant="contained"
            onClick={handlePrint}
          >
            印刷
          </Button>
        </DialogActions>
      </Dialog>

      {/* 印刷用スタイル */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #print-receipt-history, #print-receipt-history * {
              visibility: visible;
            }
            #print-receipt-history {
              position: absolute;
              left: 0;
              top: 0;
              width: 80mm;
              padding: 5mm;
            }
          }
        `}
      </style>

      {/* 印刷用レシート（テーブル単位） */}
      {selectedTableGroup && (
        <Box
          id="print-receipt-history"
          sx={{
            display: 'none',
            '@media print': {
              display: 'block',
            },
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              領収書
            </Typography>
            <Typography variant="body2">
              {selectedTableGroup.lastOrderTime?.toLocaleString('ja-JP')}
            </Typography>
            {selectedTableGroup.tableNumber !== "-" && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {selectedTableGroup.area && `${selectedTableGroup.area} `}
                テーブル {selectedTableGroup.tableNumber}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

          {getAllItemsFromGroup(selectedTableGroup).map((item, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                {item.displayName} x{item.quantity}
              </Typography>
              <Typography variant="body2">
                ¥{(item.price * item.quantity).toLocaleString()}
              </Typography>
            </Box>
          ))}

          <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>合計</Typography>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              ¥{selectedTableGroup.totalAmount.toLocaleString()}
            </Typography>
          </Box>

          {selectedTableGroup.tipTotal > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2">投げ銭</Typography>
                <Typography variant="body2">¥{selectedTableGroup.tipTotal.toLocaleString()}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>総計</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  ¥{(selectedTableGroup.totalAmount + selectedTableGroup.tipTotal).toLocaleString()}
                </Typography>
              </Box>
            </>
          )}

          <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="caption" display="block">
              ありがとうございました
            </Typography>
            <Typography variant="caption" display="block">
              またのご来店をお待ちしております
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OrderHistory;
